/**
 * `/<tenantSlug>/admin/data` — Data Explorer.
 *
 * Side-by-side visual verification of every Ken data layer for the
 * active tenant. Shows live counts at L1 events / L1 canonical / L1
 * custom fields / L1→L2 inbox / L2 nodes / L1 embeddings, plus a recent-
 * events feed and a per-customer pipeline audit table (every row green
 * if the pipeline fired end-to-end; red dots for any gap).
 *
 * Used as the operator's "did the data actually land?" surface — no SSH,
 * no psql. Pairs with the cheat-sheet at
 * docs/reference/KEN_DATA_LAYOUT_CHEATSHEET.md.
 *
 * Backed by /api/shell/_internal/data/{counts,events,pipeline-audit},
 * which call the SECURITY DEFINER aggregates in migration 0067 so
 * cockpit_role never reads L1/L2 tables directly.
 */

import { useApiClient } from "@ken/api-client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmpty,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from "@ken/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactElement } from "react";

export const Route = createFileRoute("/$tenantSlug/admin/data")({
  component: AdminDataRoute,
});

// ---------------------------------------------------------------------------
// API shapes (mirror the FastAPI Pydantic models)
// ---------------------------------------------------------------------------

interface LayerCounts {
  events_total: number;
  events_last_24h: number;
  canonical_customers: number;
  custom_field_values: number;
  custom_field_defs: number;
  inbox_pending: number;
  inbox_processing: number;
  inbox_done: number;
  inbox_failed: number;
  l2_nodes: number;
  customer_embeddings: number;
  // Mig 0609: projection errors still open, at any age. The old
  // projection_errors_24h counted by when a record failed and ignored whether
  // it had been repaired, so an unfixed error left the tile after a day.
  projection_errors_unrepaired: number;
  age_graph_exists: boolean;
}

interface RecentEvent {
  event_id: string;
  timestamp: string;
  kind: string;
  source: string;
  source_ref: string;
  name_hint: string | null;
}

interface PipelineAuditRow {
  customer_id: string;
  customer_name: string;
  source_ref: string;
  event_exists: boolean;
  custom_field_count: number;
  inbox_status: string | null;
  l2_node_exists: boolean;
  embedding_exists: boolean;
  updated_at: string;
}

interface LayerStatus {
  ok: boolean;
  detail: Record<string, unknown>;
}

/**
 * One scheduled job that has failed at least twice in a row (T124 / PRD 01 E5).
 *
 * These failures have always been recorded — pg_cron writes every one of them
 * to `cron.job_run_details` with its error message — and nothing in Ken had
 * ever read that table. Two jobs had been failing every hour and every night
 * since May, 2,595 failures between them, and the first anyone knew of it was
 * a manual audit in August.
 */
interface FailingJob {
  jobname: string;
  schedule: string;
  is_active: boolean;
  consecutive_failures: number;
  runs_recorded: number;
  last_status: string | null;
  last_run_at: string | null;
  last_error: string | null;
  failing_since: string | null;
}

interface JobHealth {
  failing: FailingJob[];
  threshold: number;
  jobs_watched: number;
  runs_recorded: number;
  /** True when the empty list means "we could not ask", not "all is well". */
  degraded: boolean;
  degraded_reason: string | null;
  checked_at: string | null;
}

interface LayerHealth {
  l1: LayerStatus;
  l2: LayerStatus;
  l3: LayerStatus;
  l4: LayerStatus;
  l5: LayerStatus;
  l6: LayerStatus;
  l7: LayerStatus;
}

// ---------------------------------------------------------------------------
// Tiny API client wrapper (mirrors modules/customers/frontend/src/api/client.ts)
// ---------------------------------------------------------------------------

type AnyClient = {
  GET: (path: string) => Promise<{ data?: unknown; response: Response }>;
};

async function getCounts(client: unknown): Promise<LayerCounts> {
  const r = await (client as AnyClient).GET("/api/shell/_internal/data/counts");
  if (!r.response.ok) throw new Error(`counts ${r.response.status}`);
  return r.data as LayerCounts;
}

async function getEvents(client: unknown): Promise<RecentEvent[]> {
  const r = await (client as AnyClient).GET("/api/shell/_internal/data/events");
  if (!r.response.ok) throw new Error(`events ${r.response.status}`);
  return ((r.data as { events: RecentEvent[] }).events) ?? [];
}

async function getAudit(client: unknown): Promise<PipelineAuditRow[]> {
  const r = await (client as AnyClient).GET("/api/shell/_internal/data/pipeline-audit");
  if (!r.response.ok) throw new Error(`audit ${r.response.status}`);
  return ((r.data as { rows: PipelineAuditRow[] }).rows) ?? [];
}

async function getLayerHealth(client: unknown): Promise<LayerHealth> {
  const r = await (client as AnyClient).GET(
    "/api/shell/_internal/data/layer-health",
  );
  if (!r.response.ok) throw new Error(`layer-health ${r.response.status}`);
  return r.data as LayerHealth;
}

async function getJobHealth(client: unknown): Promise<JobHealth> {
  const r = await (client as AnyClient).GET(
    "/api/shell/_internal/data/job-health",
  );
  if (!r.response.ok) throw new Error(`job-health ${r.response.status}`);
  return r.data as JobHealth;
}

// ---------------------------------------------------------------------------
// UI bits
// ---------------------------------------------------------------------------

function CountCard({
  title,
  value,
  hint,
  tone,
  testid,
}: {
  title: string;
  value: number | string;
  hint?: string;
  tone?: "default" | "warn" | "ok" | "bad";
  testid: string;
}): ReactElement {
  const toneClass =
    tone === "bad"
      ? "text-[var(--color-accent-red)]"
      : tone === "warn"
        ? "text-[var(--color-accent-amber)]"
        : tone === "ok"
          ? "text-[var(--color-accent-green)]"
          : "text-[var(--color-text-strong)]";
  return (
    <Card surface="glass-strong" padding="md" data-testid={testid}>
      <CardContent>
        <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-soft)] font-semibold">
          {title}
        </div>
        <div className={`text-[28px] font-bold tabular-nums mt-1 ${toneClass}`}>
          {value}
        </div>
        {hint ? (
          <div className="text-xs text-[var(--color-text-soft)] mt-0.5">{hint}</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function StatusChip({ ok, label }: { ok: boolean; label: string }): ReactElement {
  return (
    <Chip
      tone={ok ? "green" : "red"}
      data-testid={`admin-data-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
      // Phase J: WCAG 1.4.1 — color must not be the only signal.
      // Tone alone (green/red) wouldn't tell a screen-reader user
      // whether the layer was healthy. The aria-label states it.
      aria-label={`${label}: ${ok ? "ok" : "failed"}`}
    >
      <span aria-hidden="true">{ok ? "✓" : "✗"}</span> {label}
    </Chip>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function AdminDataRoute(): ReactElement {
  const apiClient = useApiClient();

  const countsQ = useQuery({
    queryKey: ["admin-data", "counts"],
    queryFn: () => getCounts(apiClient),
    refetchInterval: 5_000,
  });
  const eventsQ = useQuery({
    queryKey: ["admin-data", "events"],
    queryFn: () => getEvents(apiClient),
    refetchInterval: 5_000,
  });
  const auditQ = useQuery({
    queryKey: ["admin-data", "audit"],
    queryFn: () => getAudit(apiClient),
    refetchInterval: 10_000,
  });
  const layerHealthQ = useQuery({
    queryKey: ["admin-data", "layer-health"],
    queryFn: () => getLayerHealth(apiClient),
    refetchInterval: 5_000,
  });
  const jobHealthQ = useQuery({
    queryKey: ["admin-data", "job-health"],
    queryFn: () => getJobHealth(apiClient),
    refetchInterval: 30_000,
  });

  const counts = countsQ.data;
  const events = eventsQ.data ?? [];
  const audit = auditQ.data ?? [];
  const layerHealth = layerHealthQ.data;
  const jobHealth = jobHealthQ.data;

  // Three states, never two. "No failing jobs" and "could not find out" must
  // not render the same, which is the entire defect T124 closes.
  const jobHealthState: "checking" | "blind" | "failing" | "healthy" =
    jobHealthQ.isError
      ? "blind"
      : jobHealth === undefined
        ? "checking"
        : jobHealth.degraded
          ? "blind"
          : jobHealth.failing.length > 0
            ? "failing"
            : "healthy";

  // Header — overall pipeline health chips
  const pipelineHealthy =
    counts !== undefined &&
    counts.age_graph_exists &&
    counts.inbox_pending === 0 &&
    counts.inbox_processing === 0 &&
    counts.inbox_failed === 0 &&
    counts.projection_errors_unrepaired === 0;

  return (
    <div className="flex flex-col gap-6" data-testid="admin-data-page">
      <header className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-soft)] font-semibold">
            Admin · Internal
          </div>
          <h1 className="text-[26px] font-bold tracking-tight">Data Explorer</h1>
          <div className="text-sm text-[var(--color-text-soft)] mt-1">
            Live snapshot of every layer for this tenant. Refreshes every 5 s.
          </div>
        </div>
        <div className="flex gap-2">
          {counts ? (
            <>
              <StatusChip ok={counts.age_graph_exists} label="AGE graph" />
              <StatusChip ok={counts.inbox_failed === 0} label="No failed inbox rows" />
              <StatusChip
                ok={counts.projection_errors_unrepaired === 0}
                label="No open projection errors"
              />
              <Chip tone={pipelineHealthy ? "green" : "amber"}>
                {pipelineHealthy ? "All layers healthy" : "Investigate"}
              </Chip>
            </>
          ) : null}
        </div>
      </header>

      {/* Full-stack layer-health chips — one per layer, green if /layer-health says ok. */}
      <section
        className="flex flex-wrap gap-2"
        data-testid="admin-data-layer-health-row"
      >
        {(["l1", "l2", "l3", "l4", "l5", "l6", "l7"] as const).map((key) => {
          const status = layerHealth ? layerHealth[key] : undefined;
          const ok = Boolean(status?.ok);
          return (
            <Chip
              key={key}
              tone={status === undefined ? "default" : ok ? "green" : "red"}
              data-testid={`admin-data-layer-chip-${key}`}
              aria-label={`${key.toUpperCase()}: ${
                status === undefined ? "checking" : ok ? "healthy" : "unhealthy"
              }`}
            >
              <span aria-hidden="true">
                {status === undefined ? "…" : ok ? "✓" : "✗"}
              </span>{" "}
              {key.toUpperCase()}
            </Chip>
          );
        })}
      </section>

      {/*
        Background jobs (T124 / PRD 01 E5). Always rendered, and never in fewer
        than three states: healthy, failing, or unable to find out. A panel that
        showed nothing when it could not check would be one more silent thing
        on a page whose whole purpose is to end silence.
      */}
      <section data-testid="admin-data-job-health">
        <Card
          surface="glass-strong"
          padding="md"
          data-testid="admin-data-job-health-card"
        >
          <CardHeader>
            <CardTitle>Background jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 flex-wrap">
              <Chip
                tone={
                  jobHealthState === "failing"
                    ? "red"
                    : jobHealthState === "blind"
                      ? "amber"
                      : jobHealthState === "healthy"
                        ? "green"
                        : "default"
                }
                data-testid={`admin-data-job-health-chip-${jobHealthState}`}
                aria-label={
                  jobHealthState === "failing"
                    ? `${jobHealth?.failing.length ?? 0} scheduled jobs failing repeatedly`
                    : jobHealthState === "blind"
                      ? "scheduled job health could not be checked"
                      : jobHealthState === "healthy"
                        ? "no scheduled job is failing repeatedly"
                        : "checking scheduled job health"
                }
              >
                <span aria-hidden="true">
                  {jobHealthState === "failing"
                    ? "✗"
                    : jobHealthState === "blind"
                      ? "?"
                      : jobHealthState === "healthy"
                        ? "✓"
                        : "…"}
                </span>{" "}
                {jobHealthState === "failing"
                  ? `${jobHealth?.failing.length ?? 0} failing`
                  : jobHealthState === "blind"
                    ? "Cannot check"
                    : jobHealthState === "healthy"
                      ? `${jobHealth?.jobs_watched ?? 0} jobs, none failing`
                      : "Checking…"}
              </Chip>
              {jobHealth && !jobHealth.degraded ? (
                <span className="text-xs text-[var(--color-text-soft)]">
                  Alerting at {jobHealth.threshold} failures in a row ·{" "}
                  {jobHealth.runs_recorded.toLocaleString()} runs on record
                </span>
              ) : null}
            </div>

            {jobHealthState === "blind" ? (
              <div
                className="mt-3 text-sm text-[var(--color-accent-amber)]"
                data-testid="admin-data-job-health-degraded"
                role="status"
              >
                {jobHealthQ.isError
                  ? "The scheduled-job health check could not be reached. Nothing here is evidence that the jobs are working."
                  : (jobHealth?.degraded_reason ??
                    "The scheduled-job health check could not run.")}
              </div>
            ) : null}

            {jobHealthState === "failing" && jobHealth ? (
              <DataTable className="mt-3">
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableHead>Job</DataTableHead>
                    <DataTableHead>Failures in a row</DataTableHead>
                    <DataTableHead>Failing since</DataTableHead>
                    <DataTableHead>Last error</DataTableHead>
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {jobHealth.failing.map((job) => (
                    <DataTableRow
                      key={job.jobname}
                      data-testid={`admin-data-job-health-row-${job.jobname}`}
                    >
                      <DataTableCell>{job.jobname}</DataTableCell>
                      <DataTableCell className="tabular-nums">
                        {job.consecutive_failures}
                      </DataTableCell>
                      <DataTableCell>
                        {job.failing_since
                          ? new Date(job.failing_since).toLocaleString()
                          : "—"}
                      </DataTableCell>
                      <DataTableCell className="text-xs">
                        {job.last_error ?? "—"}
                      </DataTableCell>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </DataTable>
            ) : null}
          </CardContent>
        </Card>
      </section>

      {/* Per-layer count grid */}
      <section
        className="grid grid-cols-4 gap-3"
        data-testid="admin-data-counts-grid"
      >
        <CountCard
          title="L1 Events (total)"
          value={counts?.events_total ?? "…"}
          hint={`${counts?.events_last_24h ?? 0} in last 24 h`}
          testid="admin-data-count-events"
        />
        <CountCard
          title="L1 Canonical customers"
          value={counts?.canonical_customers ?? "…"}
          testid="admin-data-count-canonical"
        />
        <CountCard
          title="L1 Custom field values"
          value={counts?.custom_field_values ?? "…"}
          hint={`${counts?.custom_field_defs ?? 0} field definitions`}
          testid="admin-data-count-customfields"
        />
        <CountCard
          title="L1 Embeddings (customers)"
          value={counts?.customer_embeddings ?? "…"}
          tone={
            counts !== undefined &&
            counts.customer_embeddings < counts.canonical_customers
              ? "warn"
              : "ok"
          }
          hint={
            counts !== undefined &&
            counts.customer_embeddings < counts.canonical_customers
              ? "embedding worker behind"
              : undefined
          }
          testid="admin-data-count-embeddings"
        />
        <CountCard
          title="L2 Inbox · pending"
          value={counts?.inbox_pending ?? "…"}
          tone={counts && counts.inbox_pending > 0 ? "warn" : "ok"}
          testid="admin-data-count-inbox-pending"
        />
        <CountCard
          title="L2 Inbox · processing"
          value={counts?.inbox_processing ?? "…"}
          tone={counts && counts.inbox_processing > 0 ? "warn" : "ok"}
          testid="admin-data-count-inbox-processing"
        />
        <CountCard
          title="L2 Inbox · done"
          value={counts?.inbox_done ?? "…"}
          tone="ok"
          testid="admin-data-count-inbox-done"
        />
        <CountCard
          title="L2 Inbox · failed"
          value={counts?.inbox_failed ?? "…"}
          tone={counts && counts.inbox_failed > 0 ? "bad" : "ok"}
          testid="admin-data-count-inbox-failed"
        />
        <CountCard
          title="L2 Graph nodes"
          value={counts?.l2_nodes ?? "…"}
          testid="admin-data-count-l2-nodes"
        />
        <CountCard
          title="L1 Projection errors (open)"
          value={counts?.projection_errors_unrepaired ?? "…"}
          tone={counts && counts.projection_errors_unrepaired > 0 ? "bad" : "ok"}
          testid="admin-data-count-projection-errors"
        />
      </section>

      {/* Pipeline audit table */}
      <section>
        <Card surface="glass-strong">
          <CardHeader>
            <CardTitle>Per-customer pipeline state</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Customer</DataTableHead>
                  <DataTableHead className="text-center">Event</DataTableHead>
                  <DataTableHead className="text-center">
                    Custom fields
                  </DataTableHead>
                  <DataTableHead className="text-center">Inbox</DataTableHead>
                  <DataTableHead className="text-center">L2 node</DataTableHead>
                  <DataTableHead className="text-center">Embedding</DataTableHead>
                  <DataTableHead>Updated</DataTableHead>
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {auditQ.isLoading ? (
                  <DataTableRow>
                    <DataTableCell colSpan={7}>
                      <DataTableEmpty>Loading…</DataTableEmpty>
                    </DataTableCell>
                  </DataTableRow>
                ) : audit.length === 0 ? (
                  <DataTableRow>
                    <DataTableCell colSpan={7}>
                      <DataTableEmpty>No customers yet.</DataTableEmpty>
                    </DataTableCell>
                  </DataTableRow>
                ) : (
                  audit.map((row) => {
                    const okGreen = (b: boolean, label: string) => (
                      <span
                        // Phase J: WCAG 1.4.1 — colour alone isn't enough.
                        // aria-label states "ok"/"missing" so screen readers
                        // hear the status; the glyph is decorative.
                        aria-label={`${label}: ${b ? "ok" : "missing"}`}
                        role="img"
                        className={
                          b
                            ? "text-[var(--color-accent-green)]"
                            : "text-[var(--color-accent-red)]"
                        }
                      >
                        <span aria-hidden="true">{b ? "✓" : "✗"}</span>
                      </span>
                    );
                    const inboxOk = row.inbox_status === "done";
                    return (
                      <DataTableRow
                        key={row.customer_id}
                        data-testid={`admin-data-audit-row-${row.customer_id}`}
                      >
                        <DataTableCell className="font-medium">
                          {row.customer_name}
                          <div className="text-xs text-[var(--color-text-soft)]">
                            {row.source_ref}
                          </div>
                        </DataTableCell>
                        <DataTableCell className="text-center">
                          {okGreen(row.event_exists, "Event")}
                        </DataTableCell>
                        <DataTableCell className="text-center tabular-nums">
                          {row.custom_field_count}
                        </DataTableCell>
                        <DataTableCell className="text-center">
                          <Chip
                            tone={
                              inboxOk
                                ? "green"
                                : row.inbox_status === "failed"
                                  ? "red"
                                  : row.inbox_status
                                    ? "amber"
                                    : "default"
                            }
                          >
                            {row.inbox_status ?? "—"}
                          </Chip>
                        </DataTableCell>
                        <DataTableCell className="text-center">
                          {okGreen(row.l2_node_exists, "L2 node")}
                        </DataTableCell>
                        <DataTableCell className="text-center">
                          {okGreen(row.embedding_exists, "Embedding")}
                        </DataTableCell>
                        <DataTableCell className="text-xs text-[var(--color-text-soft)]">
                          {new Date(row.updated_at).toLocaleString()}
                        </DataTableCell>
                      </DataTableRow>
                    );
                  })
                )}
              </DataTableBody>
            </DataTable>
          </CardContent>
        </Card>
      </section>

      {/* Recent events feed */}
      <section>
        <Card surface="glass-strong">
          <CardHeader>
            <CardTitle>Recent L1 events (last 50)</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Timestamp</DataTableHead>
                  <DataTableHead>Kind</DataTableHead>
                  <DataTableHead>Source</DataTableHead>
                  <DataTableHead>Source ref</DataTableHead>
                  <DataTableHead>Name</DataTableHead>
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {events.length === 0 ? (
                  <DataTableRow>
                    <DataTableCell colSpan={5}>
                      <DataTableEmpty>
                        {eventsQ.isLoading ? "Loading…" : "No events yet."}
                      </DataTableEmpty>
                    </DataTableCell>
                  </DataTableRow>
                ) : (
                  events.map((e) => (
                    <DataTableRow
                      key={e.event_id}
                      data-testid={`admin-data-event-row-${e.event_id}`}
                    >
                      <DataTableCell className="text-xs text-[var(--color-text-soft)]">
                        {new Date(e.timestamp).toLocaleString()}
                      </DataTableCell>
                      <DataTableCell>
                        <code className="text-xs">{e.kind}</code>
                      </DataTableCell>
                      <DataTableCell>{e.source}</DataTableCell>
                      <DataTableCell className="text-xs">
                        <code>{e.source_ref}</code>
                      </DataTableCell>
                      <DataTableCell>{e.name_hint ?? ""}</DataTableCell>
                    </DataTableRow>
                  ))
                )}
              </DataTableBody>
            </DataTable>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
